import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { ModelInfoCacheService } from '../services/intellijetnet/model-info-cache.service'
import { AircraftService } from '../services/intellijetnet/aircraft.service'
import { Aircraft, Event } from '../types/intellijetnet'
import { capitalizeMakeName, formatModelName } from '../utils/formatters'
import { useTransactionsFiltered, useEventsFiltered } from '../hooks/intellijetnet'
import { useAuth } from '../contexts/AuthContext'
import { useChatContext } from '../contexts/ChatContext'
import { useProfile } from '../hooks/useProfile'
import { supabase } from '../lib/supabase'
import { MarketTable } from '../components/MarketTable'
import MarketTabs from '../components/market/MarketTabs'
import MarketModals from '../components/market/MarketModals'
import { MarketCustomColumn } from '../types/database'
import { MarketCustomColumnsService } from '../services/market-custom-columns.service'
import ColumnVisibilityDropdown from '../components/market/ColumnVisibilityDropdown'
import LegendDropdown from '../components/market/LegendDropdown'
import HiddenTransactionsDropdown from '../components/market/HiddenTransactionsDropdown'
import { EditModeProvider, useEditMode } from '../contexts/EditModeContext'
import EditModeToggle from '../components/market/EditModeToggle'
import { useMarketColumns } from '../hooks/useMarketColumns'
import { useMarketTabs } from '../hooks/useMarketTabs'
import { useMarketHiddenTransactions } from '../hooks/useMarketHiddenTransactions'
import { useMarketMovement } from '../hooks/useMarketMovement'
import { useMarketSoldData } from '../hooks/useMarketSoldData'
import { useMarketOffMarket } from '../hooks/useMarketOffMarket'
import { useMarketPDFExport } from '../hooks/useMarketPDFExport'
import { useMarketFiltering } from '../hooks/useMarketFiltering'
import { FULL_SALE_TRANSACTION_TYPES } from '../constants/full-sale-transaction-types'

// Inner component that uses the EditModeContext
function MarketContent() {
  const { modelId } = useParams<{ modelId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { profile } = useProfile()
  const { isEditMode } = useEditMode()
  const { setChatHidden } = useChatContext()

  // Core market data state
  const [modelInfo, setModelInfo] = useState<{
    model_id: number
    make_name: string
    model_name: string
    display_name: string
  } | null>(null)
  const [aircraft, setAircraft] = useState<Aircraft[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [removedFromMarketAircraft, setRemovedFromMarketAircraft] = useState<{ aircraft: Aircraft; event: Event | null }[]>([])
  const [canConfigureMarket, setCanConfigureMarket] = useState(false)
  const [customColumns, setCustomColumns] = useState<MarketCustomColumn[]>([])

  const numericModelId = modelId ? parseInt(modelId, 10) : undefined

  // --- Custom hooks (extracted from this file) ---
  const {
    hiddenColumns, showCellBorders, historicalMonths, columnVisibility,
    handleToggleColumn, handleResetToDefaults, handleToggleCellBorders, handleHistoricalMonthsChange,
  } = useMarketColumns()

  const {
    hiddenTransactions, handleHideTransaction, handleUnhideTransaction,
  } = useMarketHiddenTransactions(numericModelId)

  const {
    excludedFromForSale, excludedFromSold, excludedFromRemoved,
    addedToForSale, addedToSold, addedToRemoved,
  } = useMarketMovement(modelId)

  // Calculate date ranges for different tables
  const endDate = new Date().toISOString().split('T')[0]
  const sixMonthsAgo = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  // No date constraints - get all historical transaction data
  const transactionDateRange = useMemo(() => {
    // Use a very early start date to get all historical data
    const startDate = '1990-01-01'
    const endDate = new Date().toISOString().split('T')[0]
    return { startDate, endDate }
  }, [])

  const { data: transactions, loading: transactionsLoading } = useTransactionsFiltered({
    modelId: numericModelId,
    startDate: transactionDateRange.startDate,
    endDate: transactionDateRange.endDate,
    transactionTypes: FULL_SALE_TRANSACTION_TYPES as unknown as string[]
  })
  
  // Use events hook for "Off Market" events with 6-month date range
  const { data: offMarketEvents, loading: eventsLoading } = useEventsFiltered({
    modelId: numericModelId,
    startDate: sixMonthsAgo,
    endDate,
    subject: 'Off Market'
  })

  // Use events hook for "Full Sale" events to catch sales not recorded in transactions table
  // Some sales in IntelliJetNet are only recorded as events (subject="Full Sale") without
  // a corresponding entry in the transactions table. This ensures those sales are detected.
  const { data: fullSaleEvents, loading: fullSaleEventsLoading } = useEventsFiltered({
    modelId: numericModelId,
    startDate: transactionDateRange.startDate,
    endDate: transactionDateRange.endDate,
    subject: 'Full Sale'
  })


  // --- Tabs & Navigation ---
  const {
    activeTab, setExpandedTables, hasRestoredState, restoreState,
    handleTabChange, handleNavigateToDetail,
  } = useMarketTabs({ modelId, loading, transactionsLoading, eventsLoading })

  // --- Sold Data Processing ---
  const { recentlySoldAircraft, createConsolidatedSnapshots } = useMarketSoldData({
    numericModelId, transactions, transactionsLoading, fullSaleEvents, fullSaleEventsLoading,
  })




  // Check if user can configure this market
  useEffect(() => {
    const checkMarketAccess = async () => {
      if (!user || !profile || !modelId) return
      
      // Admins and global admins can always configure
      if (profile.account_type === 'admin' || profile.account_type === 'global_admin') {
        setCanConfigureMarket(true)
        return
      }
      
      // Check if employee has access to this market
      if (profile.account_type === 'employee') {
        try {
          // Use maybeSingle() instead of single() to avoid 406 error when no rows found
          const { data: marketAssignment, error: assignmentError } = await supabase
            .from('market_assignments')
            .select('id')
            .eq('user_id', user.id)
            .eq('model_id', parseInt(modelId, 10))
            .maybeSingle()

          // Set access based on whether assignment exists (and no RLS error)
          setCanConfigureMarket(!assignmentError && !!marketAssignment)
        } catch (err) {
          setCanConfigureMarket(false)
        }
      }
    }
    
    checkMarketAccess()
  }, [user, profile, modelId])

  
  // Fetch aircraft details for off market events
  useEffect(() => {
    if (offMarketEvents && offMarketEvents.length > 0) {
      fetchRemovedFromMarketAircraft(offMarketEvents)
    } else {
      setRemovedFromMarketAircraft([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offMarketEvents])

  // Load custom columns for this model
  useEffect(() => {
    const loadCustomColumns = async () => {
      if (numericModelId) {
        try {
          const columns = await MarketCustomColumnsService.getByModelId(numericModelId)
          setCustomColumns(columns)
        } catch (error) {
          // Error loading custom columns - silently ignore
        }
      }
    }
    loadCustomColumns()
  }, [numericModelId])



  // Manual function to create snapshots for testing
  const handleCreateSnapshots = async () => {
    if (transactions && transactions.length > 0) {
      await createConsolidatedSnapshots(transactions)
    }
  }




  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const numericModelId = parseInt(modelId!, 10)
      if (isNaN(numericModelId)) {
        throw new Error('Invalid model ID')
      }

      // Fetch model info
      const info = await ModelInfoCacheService.getModelInfo(numericModelId)
      if (!info) {
        throw new Error('Model not found')
      }
      setModelInfo(info)

      // Fetch aircraft for this model that are for sale
      // Select all fields needed for change detection and display
      const aircraftData = await AircraftService.query()
        .select('*') // Select all fields for change detection
        .eq('model_id', numericModelId)
        .eq('forsale', true)
        .eq('is_active', true)
        .order('yearmfr', false)
        .execute<Aircraft>()
      setAircraft(aircraftData.data)

    } catch (err: any) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  // --- Off-Market Data ---
  const {
    offMarketAircraftData, showAircraftSearchModal, setShowAircraftSearchModal,
    selectedAircraftForForm, fetchOffMarketData,
    handleSelectAircraft, handleOffMarketSuccess, handleOffMarketCancel,
  } = useMarketOffMarket({
    modelId, aircraft, recentlySoldAircraft,
    onRefreshData: fetchData,
    onError: (msg: string) => setError(msg),
  })

  // Main data loading effect
  useEffect(() => {
    if (modelId) {
      fetchData()
      fetchOffMarketData()
      if (!hasRestoredState) {
        restoreState()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId])



  const fetchRemovedFromMarketAircraft = async (events: Event[]) => {
    try {
      // Filter out events with invalid (numeric) event IDs to prevent navigation issues
      const validEvents = events.filter(e => {
        const isValidUUID = !/^\d+$/.test(e.event_id)
        return isValidUUID
      })

      // Create a map of aircraft_id to event for easy lookup
      const eventMap = new Map<number, Event>()
      validEvents.forEach(e => eventMap.set(e.aircraft_id, e))

      // Get unique aircraft IDs from valid events
      const aircraftIds = [...new Set(validEvents.map(e => e.aircraft_id))]

      // Fetch aircraft details for each ID
      const aircraftPromises = aircraftIds.map(id =>
        AircraftService.getById(id)
      )

      const aircraftResults = await Promise.all(aircraftPromises)
      const validAircraft = aircraftResults.filter((a): a is Aircraft => a !== null)

      // Filter out aircraft that are currently for sale
      const forSaleAircraftIds = new Set(aircraft.map(a => a.aircraft_id))
      const notForSaleAircraft = validAircraft.filter(a => !forSaleAircraftIds.has(a.aircraft_id))

      // Filter out aircraft that were recently sold
      const recentlySoldAircraftIds = new Set(recentlySoldAircraft.map(item => item.aircraft.aircraft_id))
      const notSoldAircraft = notForSaleAircraft.filter(a => !recentlySoldAircraftIds.has(a.aircraft_id))

      // Create combined data with aircraft and event info
      // Only include aircraft that have valid events (this ensures no aircraft with null events make it to the table)
      const combinedData = notSoldAircraft
        .map(aircraft => ({
          aircraft,
          event: eventMap.get(aircraft.aircraft_id) || null
        }))
        .filter(item => item.event !== null) // Remove any aircraft without valid events

      // Sort by event date (newest first)
      combinedData.sort((a, b) => {
        const dateA = a.event?.date || ''
        const dateB = b.event?.date || ''
        return dateB.localeCompare(dateA)
      })

      setRemovedFromMarketAircraft(combinedData)
    } catch (err) {
      // Error fetching removed from market aircraft
    }
  }

  // --- Filtering Logic (extracted hook) ---
  const {
    filteredRecentlySoldAircraft,
    hiddenIdentifiers,
    visibleRecentlySoldAircraft,
    filteredRemovedFromMarketAircraft,
    filteredOffMarketSold,
    filteredOffMarketRemoved,
    filteredOffMarketForSale,
    soldAircraftIds,
    filteredForSaleApiAircraft,
  } = useMarketFiltering({
    historicalMonths,
    recentlySoldAircraft,
    removedFromMarketAircraft,
    offMarketAircraftData,
    aircraft,
    hiddenTransactions,
  })

  // Memoize the header actions to pass to Layout (must be before early returns)
  const headerActions = useMemo(() => (
    <div className="flex items-center space-x-2">
      <LegendDropdown />
      <ColumnVisibilityDropdown
        hiddenColumns={hiddenColumns}
        onToggleColumn={handleToggleColumn}
        onResetToDefaults={handleResetToDefaults}
        customColumns={customColumns}
        showCellBorders={showCellBorders}
        onToggleCellBorders={handleToggleCellBorders}
        historicalMonths={historicalMonths}
        onHistoricalMonthsChange={handleHistoricalMonthsChange}
      />
    </div>
  ), [hiddenColumns, handleToggleColumn, handleResetToDefaults, customColumns, showCellBorders, handleToggleCellBorders, historicalMonths, handleHistoricalMonthsChange])

  // --- PDF Export Hook (must be before early returns) ---
  const {
    showExportOptionsModal, setShowExportOptionsModal,
    showPDFPreviewModal, setShowPDFPreviewModal,
    previewData,
    handleExportPDF, handlePreviewPDF, handleDownloadFromPreview, handleUpdateOptionsFromPreview,
  } = useMarketPDFExport({
    modelId, modelInfo, aircraft, recentlySoldAircraft, removedFromMarketAircraft,
    offMarketAircraftData, filteredOffMarketForSale, hiddenIdentifiers, transactions, customColumns,
  })

  // Hide chat toggle when PDF Preview modal is open
  useEffect(() => {
    setChatHidden(showPDFPreviewModal)
    return () => setChatHidden(false)
  }, [showPDFPreviewModal, setChatHidden])

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-lg text-gray-600">Loading market information...</span>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  if (error || !modelInfo) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg text-red-600 mb-4">{error || 'Model not found'}</div>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout headerActions={headerActions}>
      <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 py-6">
        <div className="w-full max-w-full">
          {/* Sticky Model Header */}
          <div className="sticky top-16 z-50 bg-gray-50 -mx-2 px-2 sm:-mx-4 sm:px-4 lg:-mx-6 lg:px-6 xl:-mx-8 xl:px-8 pb-2 pt-2 -mt-6">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-gray-900 mb-2 inline-flex items-center"
            >
              ← Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-purple-950">
              {capitalizeMakeName(modelInfo.make_name)} {formatModelName(modelInfo.model_name)}
            </h1>
          </div>



          {/* Sticky Tab Navigation with Action Buttons */}
          <div className="sticky top-[9rem] z-40 bg-gray-50 pb-2 -mx-2 px-2 sm:-mx-4 sm:px-4 lg:-mx-6 lg:px-6 xl:-mx-8 xl:px-8 pt-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-3">
                <MarketTabs
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  counts={{
                    forSale: (() => {
                      // Deduplicate: off-market entries take precedence over API entries in MarketTable,
                      // so exclude API aircraft that also appear in the off-market list
                      const offMarketForSaleIds = new Set(filteredOffMarketForSale.map(item => item.aircraft.aircraft_id))
                      const apiCount = filteredForSaleApiAircraft.filter(a => !excludedFromForSale.has(a.aircraft_id) && !offMarketForSaleIds.has(a.aircraft_id)).length
                      const offMarketCount = filteredOffMarketForSale.filter(item => !excludedFromForSale.has(item.aircraft.aircraft_id)).length
                      return apiCount + offMarketCount + addedToForSale
                    })(),
                    sold: visibleRecentlySoldAircraft.filter(item => !excludedFromSold.has(item.aircraft.aircraft_id)).length + filteredOffMarketSold.filter(item => !excludedFromSold.has(item.aircraft.aircraft_id)).length + addedToSold,
                    removed: (() => {
                      // Deduplicate: off-market entries take precedence over API entries in MarketTable
                      const offMarketRemovedIds = new Set(filteredOffMarketRemoved.map(item => item.aircraft.aircraft_id))
                      const apiCount = filteredRemovedFromMarketAircraft.filter(item => !excludedFromRemoved.has(item.aircraft.aircraft_id) && !offMarketRemovedIds.has(item.aircraft.aircraft_id)).length
                      const offMarketCount = filteredOffMarketRemoved.filter(item => !excludedFromRemoved.has(item.aircraft.aircraft_id)).length
                      return apiCount + offMarketCount + addedToRemoved
                    })()
                  }}
                />
                <EditModeToggle />
              </div>
              <div className="flex flex-wrap gap-2">
                {activeTab === 'sold' && (
                  <HiddenTransactionsDropdown
                    modelId={numericModelId || 0}
                    soldData={filteredRecentlySoldAircraft}
                    hiddenTransactions={hiddenTransactions}
                    onHide={handleHideTransaction}
                    onUnhide={handleUnhideTransaction}
                  />
                )}
                <button
                  onClick={handleExportPDF}
                  disabled={loading}
                  title="Download this market summary as a PDF"
                  className="bg-blue-700 text-white hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 flex items-center space-x-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download PDF</span>
                </button>
                <button
                  onClick={() => setShowAircraftSearchModal(true)}
                  className="bg-green-700 text-white hover:bg-green-800 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150"
                >
                  Add Off Market
                </button>
                {process.env.NODE_ENV === 'development' && (
                  <button
                    onClick={handleCreateSnapshots}
                    className="bg-orange-600 text-white hover:bg-orange-700 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150"
                    title="Create snapshots for recently sold transactions"
                  >
                    Create Snapshots
                  </button>
                )}
                {canConfigureMarket && (
                  <button
                    onClick={() => navigate(`/market/${modelId}/configure`)}
                    className="bg-purple-950 text-white hover:bg-purple-900 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150"
                  >
                    Configure Market
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Active Table Content */}
          {activeTab === 'for-sale' && (
            <MarketTable
              type="for-sale"
              modelId={modelId || ''}
              title="Currently on Market"
              aircraft={filteredForSaleApiAircraft}
              offMarketAircraft={filteredOffMarketForSale}
              soldAircraftIds={soldAircraftIds}
              loading={loading}
              initialExpanded={true}
              onExpandedChange={(expanded) => setExpandedTables(prev => ({ ...prev, forSale: expanded }))}
              columnVisibility={columnVisibility}
              onNavigateToDetail={handleNavigateToDetail}
              showCellBorders={showCellBorders}
              stickyHeaderClass="top-[13.125rem]"
              isEditMode={isEditMode}
              colorScheme={{
                header: 'bg-green-800',
                headerHover: 'bg-green-700',
                headerText: 'text-green-200',
                subHeader: 'bg-green-200',
                subHeaderText: 'text-green-800',
                scrollbar: 'green'
              }}
            />
          )}

          {activeTab === 'sold' && (
            <MarketTable
              type="sold"
              modelId={modelId || ''}
              title={`Aircraft Sold - ${historicalMonths} ${historicalMonths === 1 ? 'Month' : 'Months'}`}
              data={visibleRecentlySoldAircraft}
              offMarketAircraft={filteredOffMarketSold}
              loading={transactionsLoading}
              initialExpanded={true}
              onExpandedChange={(expanded) => setExpandedTables(prev => ({ ...prev, sold: expanded }))}
              columnVisibility={columnVisibility}
              onNavigateToDetail={handleNavigateToDetail}
              showCellBorders={showCellBorders}
              stickyHeaderClass="top-[13.125rem]"
              isEditMode={isEditMode}
              colorScheme={{
                header: 'bg-blue-800',
                headerHover: 'bg-blue-700',
                headerText: 'text-blue-200',
                subHeader: 'bg-blue-200',
                subHeaderText: 'text-blue-800',
                scrollbar: 'blue'
              }}
            />
          )}

          {activeTab === 'removed' && (
            <MarketTable
              type="removed"
              modelId={modelId || ''}
              title={`Removed from Market - ${historicalMonths} ${historicalMonths === 1 ? 'Month' : 'Months'}`}
              data={filteredRemovedFromMarketAircraft}
              offMarketAircraft={filteredOffMarketRemoved}
              soldAircraftIds={soldAircraftIds}
              loading={eventsLoading}
              initialExpanded={true}
              onExpandedChange={(expanded) => setExpandedTables(prev => ({ ...prev, removed: expanded }))}
              columnVisibility={columnVisibility}
              onNavigateToDetail={handleNavigateToDetail}
              showCellBorders={showCellBorders}
              stickyHeaderClass="top-[13.125rem]"
              isEditMode={isEditMode}
              colorScheme={{
                header: 'bg-amber-800',
                headerHover: 'bg-amber-700',
                headerText: 'text-amber-200',
                subHeader: 'bg-amber-200',
                subHeaderText: 'text-amber-800',
                scrollbar: 'amber'
              }}
            />
          )}
        </div>
      </div>

      <MarketModals
        modelId={modelId!}
        modelInfo={modelInfo}
        customColumns={customColumns}
        historicalMonths={historicalMonths}
        showAircraftSearchModal={showAircraftSearchModal}
        setShowAircraftSearchModal={setShowAircraftSearchModal}
        handleSelectAircraft={handleSelectAircraft}
        selectedAircraftForForm={selectedAircraftForForm}
        handleOffMarketSuccess={handleOffMarketSuccess}
        handleOffMarketCancel={handleOffMarketCancel}
        showExportOptionsModal={showExportOptionsModal}
        setShowExportOptionsModal={setShowExportOptionsModal}
        handlePreviewPDF={handlePreviewPDF}
        showPDFPreviewModal={showPDFPreviewModal}
        setShowPDFPreviewModal={setShowPDFPreviewModal}
        handleDownloadFromPreview={handleDownloadFromPreview}
        handleUpdateOptionsFromPreview={handleUpdateOptionsFromPreview}
        previewData={previewData}
      />
    </Layout>
  )
}

// Main export wraps MarketContent with EditModeProvider
export default function Market() {
  return (
    <EditModeProvider>
      <MarketContent />
    </EditModeProvider>
  )
}